import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  LINE_DEFAULT_AI_PROMPT,
  LineSettingInputSchema,
  type LineSettingInput,
} from '@app/shared';
import { useLineSetting, useTestLine, useUpdateLine } from './lineApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function LineForm(): JSX.Element {
  const { data, isLoading, isError, error } = useLineSetting();
  const updateMutation = useUpdateLine();
  const testMutation = useTestLine();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LineSettingInput>({
    resolver: zodResolver(LineSettingInputSchema),
    defaultValues: {
      enabled: false,
      accessToken: '',
      channelSecret: '',
      aiEnabled: true,
      aiSystemPrompt: '',
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        enabled: data.enabled,
        accessToken: '',
        channelSecret: '',
        aiEnabled: data.aiEnabled,
        aiSystemPrompt: data.aiSystemPrompt || LINE_DEFAULT_AI_PROMPT,
      });
    }
  }, [data, reset]);

  const webhookUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/api/v1/line/webhook`;
  }, []);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: LineSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        // 儲存後清掉敏感欄位的輸入框,讓使用者看到「未變更」的狀態
        reset({ ...values, accessToken: '', channelSecret: '' });
      },
    });
  };

  const errMsg = (e: unknown): string => getApiErrorMessage(e, '未知錯誤');

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="grid gap-4 max-w-2xl">
      <h2 className="text-lg font-semibold">LINE AI客服</h2>
      <p className="text-sm text-slate-600">
        接 LINE 官方帳號(Messaging API)。Access Token 與 Channel Secret 以 AES-256-GCM 加密儲存。
        Webhook URL 請貼到 LINE Developers Console → Messaging API → Webhook settings,
        並開啟「Use webhook」、關閉「Auto-reply messages」。
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('enabled')} /> 啟用 LINE AI客服
        </label>

        <Row
          label={`Channel Access Token${data?.hasAccessToken ? '（已設定,留空保留原值）' : ''}`}
          error={errors.accessToken?.message}
        >
          <input
            type="password"
            {...register('accessToken')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasAccessToken ? '（留空保留原 Token）' : '（未設定）'}
          />
        </Row>

        <Row
          label={`Channel Secret${data?.hasChannelSecret ? '（已設定,留空保留原值）' : ''}`}
          error={errors.channelSecret?.message}
        >
          <input
            type="password"
            {...register('channelSecret')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasChannelSecret ? '（留空保留原 Secret）' : '（未設定）'}
          />
        </Row>

        <label className="inline-flex items-center gap-2 text-sm pt-1">
          <input type="checkbox" {...register('aiEnabled')} /> 啟用 AI 自動回覆(關閉後 LINE 訊息只進系統不自動回)
        </label>

        <Row label="AI System Prompt" error={errors.aiSystemPrompt?.message}>
          <textarea
            {...register('aiSystemPrompt')}
            rows={8}
            className={`${inputCls} font-mono text-xs`}
            placeholder={LINE_DEFAULT_AI_PROMPT}
          />
        </Row>

        <div className="grid gap-1 text-sm">
          <span className="text-slate-700">Webhook URL(唯讀)</span>
          <div className="flex gap-2">
            <input
              readOnly
              value={webhookUrl}
              className={`${inputCls} flex-1 bg-slate-50 text-slate-700`}
            />
            <button
              type="button"
              onClick={() => void onCopy()}
              className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 text-sm"
            >
              {copied ? '已複製' : '複製'}
            </button>
          </div>
          <p className="text-xs text-slate-500">
            把此網址貼到 LINE Developers Console 的 Webhook URL,並按「Verify」測試。
          </p>
        </div>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            儲存失敗：{errMsg(updateMutation.error)}
          </div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
          <button
            type="button"
            disabled={testMutation.isPending}
            onClick={() => testMutation.mutate()}
            title="驗證已儲存的 Token / Secret 可正確解密"
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            {testMutation.isPending ? '檢查中…' : '檢查設定'}
          </button>
        </div>

        {testMutation.isSuccess && testMutation.data ? (
          testMutation.data.ok ? (
            <div role="status" className="text-green-700 text-sm">✓ {testMutation.data.message}</div>
          ) : (
            <div role="alert" className="text-red-600 text-sm">✗ {testMutation.data.message}</div>
          )
        ) : null}
        {testMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">檢查失敗：{errMsg(testMutation.error)}</div>
        ) : null}
      </form>
    </section>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? <span role="alert" className="text-red-600 text-xs">{error}</span> : null}
    </label>
  );
}

const inputCls =
  'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
